import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Popover,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Visibility as VisibilityIcon,
  FilterAltOff as FilterAltOffIcon,
  CalendarMonth,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import PageHeader from './PageHeader';
import { useImagePreview } from './ImagePreviewDialog';
import { getDbTables, getDbTableColumns, queryDbTable } from '../utils/api';
import { pageFilterDatePickerSx } from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
} from '../utils/filterDefaults';

const PRIMARY = '#40518A';

const CONDITIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'greater_or_equal', label: 'Greater or equal' },
  { value: 'less_or_equal', label: 'Less or equal' },
  { value: 'is_null', label: 'Is null' },
  { value: 'is_not_null', label: 'Is not null' },
];

const SORT_ORDERS = [
  { value: 'ASC', label: 'ASC' },
  { value: 'DESC', label: 'DESC' },
];

const EMPTY_FILTER = { column: '', condition: 'equals', value: '' };
const PAGE_SIZE = 50;

const ID_COLUMN_VALUE_ERROR = 'Only int allowed';

/** Positive integer string (digits only; no decimals, signs, or letters). */
const isValidIdFilterValue = (value) => /^\d+$/.test(String(value ?? '').trim());

/**
 * Inline error for Value / Value 2 when Column is `id`.
 * Empty values never error; used when invalid input slips in (paste / autofill).
 */
const getIdColumnValueError = (filter) => {
  if (!filter || filter.column !== 'id') return '';
  if (['is_null', 'is_not_null'].includes(filter.condition)) return '';
  const trimmed = String(filter.value ?? '').trim();
  if (trimmed === '') return '';
  if (!isValidIdFilterValue(trimmed)) return ID_COLUMN_VALUE_ERROR;
  return '';
};

const TOOLBAR_CONTROL_WIDTH = 140;
const TOOLBAR_VALUE_WIDTH = 120;
const TOOLBAR_TABLE_WIDTH = 160;

const toolbarFieldSx = {
  minWidth: TOOLBAR_CONTROL_WIDTH,
  width: TOOLBAR_CONTROL_WIDTH,
  flexShrink: 0,
  '& .MuiInputBase-root': { height: 40 },
};

const toolbarValueSx = {
  minWidth: TOOLBAR_VALUE_WIDTH,
  width: TOOLBAR_VALUE_WIDTH,
  flexShrink: 0,
  '& .MuiInputBase-root': { height: 40 },
};

const toolbarTableSx = {
  minWidth: TOOLBAR_TABLE_WIDTH,
  width: TOOLBAR_TABLE_WIDTH,
  flexShrink: 0,
  '& .MuiInputBase-root': { height: 40 },
};

const ACTION_COLUMN_WIDTH = 80;

/** Sticky header cell for the right-fixed Actions column. */
const stickyActionHeaderSx = {
  position: 'sticky',
  right: 0,
  zIndex: 8,
  minWidth: ACTION_COLUMN_WIDTH,
  width: ACTION_COLUMN_WIDTH,
  maxWidth: ACTION_COLUMN_WIDTH,
  fontWeight: 'bold',
  backgroundColor: (theme) => theme.palette.grey[100],
  backgroundClip: 'padding-box',
  boxShadow: (theme) => `-4px 0 8px -4px ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
};

/** Sticky body cell for the right-fixed Actions column; syncs hover with row. */
const stickyActionCellSx = {
  position: 'sticky',
  right: 0,
  zIndex: 7,
  minWidth: ACTION_COLUMN_WIDTH,
  width: ACTION_COLUMN_WIDTH,
  maxWidth: ACTION_COLUMN_WIDTH,
  backgroundColor: (theme) => theme.palette.background.paper,
  backgroundClip: 'padding-box',
  boxShadow: (theme) => `-4px 0 8px -4px ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  '& .MuiIconButton-root': {
    position: 'relative',
    zIndex: 1,
  },
};

const dataCellSx = {
  minWidth: 140,
  maxWidth: 280,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const getDefaultSort = (tableColumns) => (
  tableColumns.includes('created_at') ? 'created_at' : (tableColumns[0] || '')
);

const ALERT_RULES_HIDDEN_GRID_COLUMNS = new Set(['conditions', 'actions']);
const ATTENDANCE_NOTIFICATIONS_HIDDEN_GRID_COLUMNS = new Set(['payload_json']);
const FACE_RECOGNITION_AUDIT_HIDDEN_GRID_COLUMNS = new Set(['request_metadata']);
const HRMS_EMPLOYEES_HIDDEN_GRID_COLUMNS = new Set(['raw_data']);
const RECORDING_SCHEDULE_HIDDEN_GRID_COLUMNS = new Set(['windows_json']);
const ROI_CONFIG_HIDDEN_GRID_COLUMNS = new Set(['crop_configuration']);
const HIDDEN_GRID_COLUMNS_BY_TABLE = {
  alert_rules: ALERT_RULES_HIDDEN_GRID_COLUMNS,
  attendance_notifications: ATTENDANCE_NOTIFICATIONS_HIDDEN_GRID_COLUMNS,
  face_recognition_audit: FACE_RECOGNITION_AUDIT_HIDDEN_GRID_COLUMNS,
  hrms_employees: HRMS_EMPLOYEES_HIDDEN_GRID_COLUMNS,
  recording_schedule: RECORDING_SCHEDULE_HIDDEN_GRID_COLUMNS,
  roi_config: ROI_CONFIG_HIDDEN_GRID_COLUMNS,
};

const IMAGE_PAYLOAD_FIELDS = new Set([
  'image',
  'image_base64',
  'photo',
  'snapshot',
  'face_image',
  'captured_image',
  'image_data',
]);

const isAlertRulesTable = (tableName) => tableName === 'alert_rules';
const isAttendanceNotificationsTable = (tableName) => tableName === 'attendance_notifications';
const isFaceRecognitionAuditTable = (tableName) => tableName === 'face_recognition_audit';
const isHrmsEmployeesTable = (tableName) => tableName === 'hrms_employees';
const isRecordingScheduleTable = (tableName) => tableName === 'recording_schedule';
const isRoiConfigTable = (tableName) => tableName === 'roi_config';

const formatFieldLabel = (key) => key
  .replace(/_/g, ' ')
  .replace(/\[(\d+)\]/g, ' $1')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const tryParseJson = (value) => {
  if (value === null || value === undefined || value === '') {
    return { parsed: null, success: true };
  }
  if (typeof value === 'object') {
    return { parsed: value, success: true };
  }
  if (typeof value !== 'string') {
    return { parsed: value, success: true };
  }
  try {
    return { parsed: JSON.parse(value), success: true };
  } catch (_) {
    return { parsed: value, success: false };
  }
};

const formatJsonFallback = (value) => {
  if (typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (_) {
    return value;
  }
};

const getPayloadFieldName = (path) => {
  const lastSegment = String(path).split('.').pop() || '';
  return lastSegment.replace(/\[\d+\]/g, '').toLowerCase();
};

const isImagePayloadField = (path) => IMAGE_PAYLOAD_FIELDS.has(getPayloadFieldName(path));

const normalizeImageSrc = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(trimmed)) {
    return trimmed;
  }

  // Treat long base64-looking values on image fields as JPEG by default.
  const compact = trimmed.replace(/\s/g, '');
  const looksLikeBase64 = compact.length > 80 && /^[A-Za-z0-9+/]+={0,2}$/.test(compact);
  return looksLikeBase64 ? `data:image/jpeg;base64,${compact}` : null;
};

const flattenPayloadEntries = (value, parentKey = '') => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => (
      flattenPayloadEntries(item, `${parentKey}[${index + 1}]`)
    ));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      const nextKey = parentKey ? `${parentKey}.${key}` : key;
      return flattenPayloadEntries(nestedValue, nextKey);
    });
  }

  return [{ key: parentKey || 'value', value }];
};

const ImagePreview = ({ label, src }) => {
  const [failed, setFailed] = React.useState(false);
  const { openImagePreview } = useImagePreview();
  const title = formatFieldLabel(label);

  const handleOpenPreview = (event) => {
    if (!src || failed) return;
    event.preventDefault();
    event.stopPropagation();
    openImagePreview({
      src,
      title,
      alt: title,
    });
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        bgcolor: 'grey.50',
        minWidth: { xs: '100%', sm: 220 },
        maxWidth: 320,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {!src || failed ? (
        <Alert severity="warning">Unable to load image</Alert>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          onClick={handleOpenPreview}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleOpenPreview(event);
            }
          }}
          sx={{ display: 'block', cursor: 'zoom-in' }}
        >
          <Box
            component="img"
            src={src}
            alt={title}
            data-no-image-preview="true"
            onError={() => setFailed(true)}
            sx={{
              width: '100%',
              maxHeight: 260,
              objectFit: 'contain',
              borderRadius: 0.5,
              display: 'block',
            }}
          />
        </Box>
      )}
      {src && !failed && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Click image to open preview.
        </Typography>
      )}
    </Box>
  );
};

const collectImageEntries = (value, parentKey = '') => {
  const src = normalizeImageSrc(value);
  if (src) {
    return [{ key: parentKey || 'image', src }];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => collectImageEntries(item, `${parentKey}[${index + 1}]`));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nestedValue]) => {
      const nextKey = parentKey ? `${parentKey}.${key}` : key;
      return collectImageEntries(nestedValue, nextKey);
    });
  }

  return [];
};

const removeImageValues = (value) => {
  if (normalizeImageSrc(value)) return undefined;

  if (Array.isArray(value)) {
    return value
      .map(removeImageValues)
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, nestedValue]) => [key, removeImageValues(nestedValue)])
        .filter(([, nestedValue]) => nestedValue !== undefined)
    );
  }

  return value;
};

const DbExplorer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [displayColumns, setDisplayColumns] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [filter1, setFilter1] = useState({ ...EMPTY_FILTER });
  const [filter2, setFilter2] = useState({ ...EMPTY_FILTER });
  const [idValueAttemptErrors, setIdValueAttemptErrors] = useState({
    filter1: false,
    filter2: false,
  });
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [viewRow, setViewRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => createTodayDateRange());
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const datePickerOpen = Boolean(datePickerAnchor);
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);

  const showIdValueAttemptError = (filterKey) => {
    setIdValueAttemptErrors((prev) => (
      prev[filterKey] ? prev : { ...prev, [filterKey]: true }
    ));
  };

  const clearIdValueAttemptError = (filterKey) => {
    setIdValueAttemptErrors((prev) => (
      prev[filterKey] ? { ...prev, [filterKey]: false } : prev
    ));
  };

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const buildFilters = useCallback(() => {
    const filters = [];
    if (filter1.column) filters.push(filter1);
    if (filter2.column) filters.push(filter2);
    return filters;
  }, [filter1, filter2]);

  const buildDateParams = useCallback((
    activeDateRange = dateRange,
    activeStartTime = startTime,
    activeEndTime = endTime,
  ) => {
    const startDate = activeDateRange?.[0]?.startDate;
    const endDate = activeDateRange?.[0]?.endDate || startDate;
    if (!startDate) return {};
    return {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate || startDate, 'yyyy-MM-dd'),
      start_time: activeStartTime || DEFAULT_START_TIME,
      end_time: activeEndTime || DEFAULT_END_TIME,
    };
  }, [dateRange, startTime, endTime]);

  const runQuery = useCallback(async (
    tableName,
    queryPage = 1,
    activeSortBy = sortBy,
    activeSortOrder = sortOrder,
    activeFilters = buildFilters(),
    activeDateParams = buildDateParams(),
  ) => {
    if (!tableName) return;
    setLoadingData(true);
    try {
      const resolvedSortBy = activeSortBy || '';
      const response = await queryDbTable({
        table: tableName,
        page: queryPage,
        pageSize: PAGE_SIZE,
        filters: activeFilters,
        sortBy: resolvedSortBy || undefined,
        sortOrder: resolvedSortBy ? activeSortOrder : undefined,
        ...activeDateParams,
      });

      if (!response.success) {
        throw new Error('Query failed');
      }

      setRows(response.data || []);
      setDisplayColumns(response.columns || []);
      setTotalRecords(response.totalRecords || 0);
      setTotalPages(response.totalPages || 0);
      setPage(response.page || queryPage);
    } catch (error) {
      console.error('Error querying table:', error);
      showSnackbar(error.message || 'Failed to load table data');
      setRows([]);
      setDisplayColumns([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoadingData(false);
    }
  }, [buildFilters, buildDateParams, sortBy, sortOrder]);

  const loadTables = useCallback(async () => {
    setLoadingTables(true);
    try {
      const response = await getDbTables();
      if (response.success) {
        setTables(response.tables || []);
      } else {
        setTables(response.tables || []);
      }
    } catch (error) {
      console.error('Error loading tables:', error);
      showSnackbar(error.message || 'Failed to load tables');
    } finally {
      setLoadingTables(false);
    }
  }, []);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const handleTableChange = async (event) => {
    const tableName = event.target.value;
    setSelectedTable(tableName);
    setFilter1({ ...EMPTY_FILTER });
    setFilter2({ ...EMPTY_FILTER });
    setIdValueAttemptErrors({ filter1: false, filter2: false });
    setRows([]);
    setDisplayColumns([]);
    setTotalRecords(0);
    setTotalPages(0);
    setPage(1);

    if (!tableName) {
      setColumns([]);
      setSortBy('');
      return;
    }

    try {
      const response = await getDbTableColumns(tableName);
      const tableColumns = response.columns || [];
      setColumns(tableColumns);

      const defaultSort = getDefaultSort(tableColumns);
      setSortBy(defaultSort);
      setSortOrder('DESC');

      // Keep the selected date when switching tables; always include it on the first load.
      await runQuery(tableName, 1, defaultSort, 'DESC', [], buildDateParams());
    } catch (error) {
      console.error('Error loading columns:', error);
      showSnackbar(error.message || 'Failed to load columns');
    }
  };

  const handleApply = () => {
    if (!selectedTable) {
      showSnackbar('Please select a table first');
      return;
    }
    if (getIdColumnValueError(filter1) || getIdColumnValueError(filter2)) {
      return;
    }
    runQuery(selectedTable, 1, sortBy, sortOrder, buildFilters(), buildDateParams());
  };

  const handleApplyDateFilter = () => {
    setDatePickerAnchor(null);
    if (!selectedTable) return;
    if (getIdColumnValueError(filter1) || getIdColumnValueError(filter2)) {
      return;
    }
    runQuery(selectedTable, 1, sortBy, sortOrder, buildFilters(), buildDateParams());
  };

  const handleClearAllFilters = () => {
    if (!selectedTable) {
      showSnackbar('Please select a table first');
      return;
    }
    const todayRange = createTodayDateRange();
    setFilter1({ ...EMPTY_FILTER });
    setFilter2({ ...EMPTY_FILTER });
    setIdValueAttemptErrors({ filter1: false, filter2: false });
    setSortBy('');
    setSortOrder('DESC');
    setDateRange(todayRange);
    setStartTime(DEFAULT_START_TIME);
    setEndTime(DEFAULT_END_TIME);
    runQuery(
      selectedTable,
      1,
      '',
      'DESC',
      [],
      buildDateParams(todayRange, DEFAULT_START_TIME, DEFAULT_END_TIME),
    );
  };

  const handlePageChange = (event, newPage) => {
    if (getIdColumnValueError(filter1) || getIdColumnValueError(filter2)) {
      return;
    }
    runQuery(selectedTable, newPage, sortBy, sortOrder, buildFilters(), buildDateParams());
  };

  const handleViewRow = (row) => {
    setViewRow(row);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewRow(null);
  };

  const hiddenGridColumns = HIDDEN_GRID_COLUMNS_BY_TABLE[selectedTable] || new Set();
  const visibleGridColumns = displayColumns.filter((column) => !hiddenGridColumns.has(column));

  const formatCellValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    const str = String(value);
    return str.length > 100 ? `${str.substring(0, 100)}…` : str;
  };

  const getFullCellValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderDataCellValue = (value) => {
    const fullValue = getFullCellValue(value);
    return (
      <Tooltip
        title={
          fullValue ? (
            <Box
              sx={{
                maxWidth: 420,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.45,
              }}
            >
              {fullValue}
            </Box>
          ) : ''
        }
        arrow
        placement="top"
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            display: 'block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatCellValue(value)}
        </Typography>
      </Tooltip>
    );
  };

  const updateFilter = (setter) => (field, value) => {
    setter((prev) => ({ ...prev, [field]: value }));
  };

  const setFilter1Field = updateFilter(setFilter1);
  const setFilter2Field = updateFilter(setFilter2);

  /** Single View action — opens the row detail dialog. */
  const renderViewAction = (row, rowIndex) => (
    <Tooltip title="View Details" arrow describeChild>
      <IconButton
        size="small"
        color="primary"
        onClick={() => handleViewRow(row)}
        aria-label={`View details for row ${rowIndex + 1}`}
        sx={{
          '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.light,
            color: (theme) => theme.palette.primary.contrastText,
          },
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderFilterControls = (filter, setField, filterKey, columnLabel, conditionLabel, valueLabel) => {
    const isIdColumn = filter.column === 'id';
    const valueError = (isIdColumn && idValueAttemptErrors[filterKey])
      ? ID_COLUMN_VALUE_ERROR
      : getIdColumnValueError(filter);

    const handleColumnChange = (event) => {
      const nextColumn = event.target.value;
      const updateFilterState = filterKey === 'filter1' ? setFilter1 : setFilter2;
      if (nextColumn === 'id') {
        // Drop non-digits left over from a previous column selection.
        updateFilterState((prev) => ({
          ...prev,
          column: nextColumn,
          value: String(prev.value ?? '').replace(/\D/g, ''),
        }));
      } else {
        setField('column', nextColumn);
      }
      clearIdValueAttemptError(filterKey);
    };

    const handleValueChange = (event) => {
      const next = event.target.value;
      if (!isIdColumn) {
        setField('value', next);
        return;
      }
      setField('value', next);
      // Clear attempt error once the field is empty or a valid integer.
      if (next === '' || isValidIdFilterValue(next)) {
        clearIdValueAttemptError(filterKey);
      }
    };

    const handleValueKeyDown = (event) => {
      if (!isIdColumn) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (
        [
          'Backspace',
          'Delete',
          'Tab',
          'Escape',
          'Enter',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ].includes(event.key)
      ) {
        return;
      }
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
        showIdValueAttemptError(filterKey);
      }
    };

    const handleValuePaste = (event) => {
      if (!isIdColumn) return;
      const pasted = event.clipboardData?.getData('text') ?? '';
      if (pasted === '' || /^\d+$/.test(pasted.trim())) {
        clearIdValueAttemptError(filterKey);
        return;
      }
      // Allow the invalid paste so the field shows it and inline validation can appear.
      event.preventDefault();
      setField('value', pasted);
      clearIdValueAttemptError(filterKey);
    };

    return (
      <>
        <FormControl size="small" sx={toolbarFieldSx}>
          <InputLabel>{columnLabel}</InputLabel>
          <Select
            label={columnLabel}
            value={filter.column}
            onChange={handleColumnChange}
            disabled={!selectedTable || columns.length === 0}
          >
            <MenuItem value="">
              <em>Select column</em>
            </MenuItem>
            {columns.map((col) => (
              <MenuItem key={col} value={col}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={toolbarFieldSx}>
          <InputLabel>{conditionLabel}</InputLabel>
          <Select
            label={conditionLabel}
            value={filter.condition}
            onChange={(e) => setField('condition', e.target.value)}
            disabled={!selectedTable}
          >
            {CONDITIONS.map((cond) => (
              <MenuItem key={cond.value} value={cond.value}>
                {cond.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!['is_null', 'is_not_null'].includes(filter.condition) ? (
          <TextField
            size="small"
            label={valueLabel}
            value={filter.value}
            onChange={handleValueChange}
            onKeyDown={handleValueKeyDown}
            onPaste={handleValuePaste}
            disabled={!selectedTable}
            error={Boolean(valueError)}
            helperText={valueError || undefined}
            inputProps={isIdColumn ? { inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
            FormHelperTextProps={{
              sx: {
                m: 0,
                mt: 0.5,
                lineHeight: 1.2,
                whiteSpace: 'normal',
              },
            }}
            sx={toolbarValueSx}
          />
        ) : (
          <Box sx={{ ...toolbarValueSx, height: 40 }} />
        )}
      </>
    );
  };

  const renderKeyValueTable = (title, data, firstColumnLabel = 'Field') => {
    const { parsed, success } = tryParseJson(data);

    if (!success) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              maxHeight: 260,
              overflow: 'auto',
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {formatJsonFallback(data)}
          </Box>
        </Box>
      );
    }

    const rows = Array.isArray(parsed)
      ? parsed.flatMap((item, index) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return Object.entries(item).map(([key, value]) => ({
            key: `${index}-${key}`,
            field: key,
            value,
          }));
        }
        return [{ key: String(index), field: `Item ${index + 1}`, value: item }];
      })
      : (parsed && typeof parsed === 'object'
        ? Object.entries(parsed).map(([key, value]) => ({ key, field: key, value }))
        : [{ key: 'value', field: 'Value', value: parsed }]);

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader aria-label={`${title} details`}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '35%', bgcolor: 'grey.100' }}>
                  {firstColumnLabel}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ key, field, value }) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                    {formatFieldLabel(field)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {value === null || value === undefined
                      ? '—'
                      : (typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderAlertRuleDetails = (row) => {
    const summaryKeys = Object.keys(row).filter(
      (key) => !ALERT_RULES_HIDDEN_GRID_COLUMNS.has(key)
    );

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Alert Rule Details</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Alert rule summary">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />
        {renderKeyValueTable('Conditions', row.conditions, 'Field')}

        <Divider sx={{ my: 3 }} />
        {renderKeyValueTable('Actions', row.actions, 'Action Type')}
      </Box>
    );
  };

  const renderPayloadDetailsTable = (payload) => {
    const entries = flattenPayloadEntries(payload);
    const nonImageEntries = entries.filter(({ key }) => !isImagePayloadField(key));

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Payload Details</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader aria-label="Notification payload details">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '35%', bgcolor: 'grey.100' }}>
                  Field
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nonImageEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No non-image payload fields found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                nonImageEntries.map(({ key, value }) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                      {formatFieldLabel(key)}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {value === null || value === undefined ? '—' : String(value)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderPayloadImages = (payload) => {
    const imageEntries = flattenPayloadEntries(payload)
      .filter(({ key }) => isImagePayloadField(key))
      .map(({ key, value }) => ({
        key,
        src: normalizeImageSrc(value),
      }));

    if (imageEntries.length === 0) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Captured Images</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {imageEntries.map(({ key, src }) => (
            <ImagePreview key={key} label={key} src={src} />
          ))}
        </Box>
      </Box>
    );
  };

  const renderAttendanceNotificationDetails = (row) => {
    const summaryKeys = Object.keys(row).filter((key) => (
      !ATTENDANCE_NOTIFICATIONS_HIDDEN_GRID_COLUMNS.has(key)
    ));
    const payloadResult = tryParseJson(row.payload_json);

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Attendance Notification</Typography>

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Attendance notification basic information">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {payloadResult.success ? (
          <>
            {renderPayloadDetailsTable(payloadResult.parsed)}
            <Divider sx={{ my: 3 }} />
            {renderPayloadImages(payloadResult.parsed)}
          </>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Payload Details</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJsonFallback(row.payload_json)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderMetadataTable = (title, rows, firstColumnLabel = 'Key') => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={`${title} metadata`}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '35%', bgcolor: 'grey.100' }}>
                {firstColumnLabel}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ key, value }) => (
              <TableRow key={key}>
                <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                  {formatFieldLabel(key)}
                </TableCell>
                <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {value === null || value === undefined ? '—' : String(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderMetadataArray = (title, value) => {
    if (value.length === 0) {
      return renderMetadataTable(title, [{ key: 'value', value: '[]' }], 'Item');
    }

    const hasObjects = value.some((item) => item && typeof item === 'object');
    if (!hasObjects) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={`${title} list`}>
              <TableBody>
                {value.map((item, index) => (
                  <TableRow key={`${title}-${index}`}>
                    <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {item === null || item === undefined ? '—' : String(item)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        {value.map((item, index) => (
          <Box key={`${title}-${index}`} sx={{ mb: 2 }}>
            {item && typeof item === 'object'
              ? renderMetadataSection(`Item ${index + 1}`, item)
              : renderMetadataTable(`Item ${index + 1}`, [{ key: 'value', value: item }])}
          </Box>
        ))}
      </Box>
    );
  };

  const renderMetadataSection = (title, value) => {
    if (Array.isArray(value)) {
      return renderMetadataArray(title, value);
    }

    if (!value || typeof value !== 'object') {
      return renderMetadataTable(title, [{ key: 'value', value }]);
    }

    const entries = Object.entries(value);
    const scalarRows = entries
      .filter(([, entryValue]) => !entryValue || typeof entryValue !== 'object')
      .map(([key, entryValue]) => ({ key, value: entryValue }));
    const nestedEntries = entries.filter(([, entryValue]) => entryValue && typeof entryValue === 'object');

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
        {scalarRows.length > 0 && renderMetadataTable(title, scalarRows)}
        {nestedEntries.map(([key, nestedValue]) => (
          <Box key={key} sx={{ mt: 2 }}>
            {renderMetadataSection(formatFieldLabel(key), nestedValue)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderFaceRecognitionAuditDetails = (row) => {
    const summaryKeys = Object.keys(row).filter((key) => (
      !FACE_RECOGNITION_AUDIT_HIDDEN_GRID_COLUMNS.has(key)
    ));
    const metadataResult = tryParseJson(row.request_metadata);

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Face Recognition Audit Details</Typography>

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Face recognition audit basic information">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {metadataResult.success ? (
          renderMetadataSection('Request Metadata', metadataResult.parsed)
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Request Metadata</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJsonFallback(row.request_metadata)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderRawDataImages = (rawData) => {
    const imageEntries = collectImageEntries(rawData);

    if (imageEntries.length === 0) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Employee Photo</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {imageEntries.map(({ key, src }) => (
            <ImagePreview key={key} label={key} src={src} />
          ))}
        </Box>
      </Box>
    );
  };

  const renderHrmsEmployeeDetails = (row) => {
    const summaryKeys = Object.keys(row).filter((key) => (
      !HRMS_EMPLOYEES_HIDDEN_GRID_COLUMNS.has(key)
    ));
    const rawDataResult = tryParseJson(row.raw_data);
    const sanitizedRawData = rawDataResult.success
      ? removeImageValues(rawDataResult.parsed)
      : null;

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Employee Details</Typography>

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="HRMS employee basic information">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {rawDataResult.success ? (
          <>
            {renderMetadataSection('Employee Raw Data', sanitizedRawData)}
            <Divider sx={{ my: 3 }} />
            {renderRawDataImages(rawDataResult.parsed)}
          </>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Employee Raw Data</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJsonFallback(row.raw_data)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const formatScheduleValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const renderDynamicObjectArrayTable = (title, items) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
          <Alert severity="info">No records found.</Alert>
        </Box>
      );
    }

    const columnsForTable = Array.from(
      items.reduce((keys, item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          Object.keys(item).forEach((key) => keys.add(key));
        } else {
          keys.add('value');
        }
        return keys;
      }, new Set())
    );

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader aria-label={`${title} schedule table`}>
            <TableHead>
              <TableRow>
                {columnsForTable.map((column) => (
                  <TableCell key={column} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                    {formatFieldLabel(column)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={`${title}-${index}`}>
                  {columnsForTable.map((column) => {
                    const value = item && typeof item === 'object' && !Array.isArray(item)
                      ? item[column]
                      : item;
                    return (
                      <TableCell key={`${title}-${index}-${column}`} sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
                        {formatScheduleValue(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {items.map((item, index) => {
          if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
          const nestedEntries = Object.entries(item).filter(([, value]) => value && typeof value === 'object');
          if (nestedEntries.length === 0) return null;
          return (
            <Box key={`${title}-nested-${index}`} sx={{ mt: 2, pl: { xs: 0, md: 2 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title} {index + 1} Details
              </Typography>
              {nestedEntries.map(([key, nestedValue]) => (
                <Box key={key} sx={{ mt: 1 }}>
                  {renderMetadataSection(formatFieldLabel(key), nestedValue)}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderScheduleJsonSections = (scheduleJson) => {
    if (Array.isArray(scheduleJson)) {
      return renderDynamicObjectArrayTable('Recording Windows', scheduleJson);
    }

    if (!scheduleJson || typeof scheduleJson !== 'object') {
      return renderMetadataSection('Recording Windows', scheduleJson);
    }

    const entries = Object.entries(scheduleJson);
    const scalarRows = entries
      .filter(([, value]) => !value || typeof value !== 'object')
      .map(([key, value]) => ({ key, value }));
    const arrayEntries = entries.filter(([, value]) => Array.isArray(value));
    const objectEntries = entries.filter(([, value]) => value && typeof value === 'object' && !Array.isArray(value));

    return (
      <Box>
        {scalarRows.length > 0 && renderMetadataTable('Schedule Configuration', scalarRows)}
        {arrayEntries.map(([key, value]) => (
          <Box key={key}>
            {renderDynamicObjectArrayTable(
              key === 'windows' ? 'Recording Windows' : formatFieldLabel(key),
              value
            )}
          </Box>
        ))}
        {objectEntries.map(([key, value]) => (
          <Box key={key} sx={{ mt: 3 }}>
            {renderMetadataSection(formatFieldLabel(key), value)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderRecordingScheduleDetails = (row) => {
    const summaryKeys = Object.keys(row).filter((key) => (
      !RECORDING_SCHEDULE_HIDDEN_GRID_COLUMNS.has(key)
    ));
    const windowsResult = tryParseJson(row.windows_json);

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Recording Schedule Details</Typography>

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Recording schedule basic information">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {windowsResult.success ? (
          renderScheduleJsonSections(windowsResult.parsed)
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Recording Windows</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJsonFallback(row.windows_json)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderRoiPointsTable = (points) => {
    if (!Array.isArray(points) || points.length === 0) return null;

    const pointKeys = Array.from(
      points.reduce((keys, point) => {
        if (point && typeof point === 'object' && !Array.isArray(point)) {
          Object.keys(point).forEach((key) => keys.add(key));
        }
        return keys;
      }, new Set(['x', 'y']))
    );

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>ROI Points</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader aria-label="ROI points">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Point No</TableCell>
                {pointKeys.map((key) => (
                  <TableCell key={key} sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {points.map((point, index) => (
                <TableRow key={`roi-point-${index}`}>
                  <TableCell>{index + 1}</TableCell>
                  {pointKeys.map((key) => (
                    <TableCell key={`roi-point-${index}-${key}`}>
                      {point && typeof point === 'object' ? formatScheduleValue(point[key]) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderRoiPreview = (config) => {
    if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
    const hasRect = ['x', 'y', 'width', 'height'].every((key) => (
      config[key] !== null && config[key] !== undefined && config[key] !== ''
    ));
    if (!hasRect) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>ROI Preview</Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
            minHeight: 220,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            ROI Configuration
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: 1.25, textAlign: 'left' }}>
            {['x', 'y', 'width', 'height', 'rotation', 'enabled'].map((key) => (
              config[key] !== undefined && config[key] !== null ? (
                <React.Fragment key={key}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatFieldLabel(key)}:</Typography>
                  <Typography variant="body2">{formatScheduleValue(config[key])}</Typography>
                </React.Fragment>
              ) : null
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderCropConfigurationSections = (config) => {
    if (Array.isArray(config)) {
      return renderDynamicObjectArrayTable('Crop Configuration', config);
    }
    if (!config || typeof config !== 'object') {
      return renderMetadataSection('Crop Configuration', config);
    }

    const entries = Object.entries(config);
    const roiPoints = Array.isArray(config.roi_points) ? config.roi_points : null;
    const scalarRows = entries
      .filter(([key, value]) => key !== 'roi_points' && (!value || typeof value !== 'object'))
      .map(([key, value]) => ({ key, value: formatScheduleValue(value) }));
    const arrayEntries = entries.filter(([key, value]) => key !== 'roi_points' && Array.isArray(value));
    const objectEntries = entries.filter(([, value]) => value && typeof value === 'object' && !Array.isArray(value));

    return (
      <Box>
        {scalarRows.length > 0 && renderMetadataTable('Crop Configuration', scalarRows, 'Field')}
        {renderRoiPreview(config)}
        {renderRoiPointsTable(roiPoints)}
        {arrayEntries.map(([key, value]) => (
          <Box key={key}>
            {renderDynamicObjectArrayTable(formatFieldLabel(key), value)}
          </Box>
        ))}
        {objectEntries.map(([key, value]) => (
          <Box key={key} sx={{ mt: 3 }}>
            {renderMetadataSection(formatFieldLabel(key), value)}
          </Box>
        ))}
      </Box>
    );
  };

  const renderRoiConfigDetails = (row) => {
    const summaryKeys = Object.keys(row).filter((key) => (
      !ROI_CONFIG_HIDDEN_GRID_COLUMNS.has(key)
    ));
    const cropResult = tryParseJson(row.crop_configuration);

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>ROI Configuration Details</Typography>

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Basic Information</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="ROI configuration basic information">
            <TableBody>
              {summaryKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                    {formatFieldLabel(key)}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {row[key] === null || row[key] === undefined ? '—' : String(row[key])}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {cropResult.success ? (
          renderCropConfigurationSections(cropResult.parsed)
        ) : (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Crop Configuration</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                maxHeight: 360,
                overflow: 'auto',
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatJsonFallback(row.crop_configuration)}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      <PageHeader
        title="DB Explorer"
        subtitle="Browse and query MySQL tables"
        icon={StorageIcon}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                alignItems: 'center',
                gap: 1.5,
                py: 0.5,
              }}
            >
              <FormControl
                size="small"
                sx={{
                  ...toolbarTableSx,
                  width: { xs: '100%', sm: TOOLBAR_TABLE_WIDTH },
                  minWidth: { xs: '100%', sm: TOOLBAR_TABLE_WIDTH },
                }}
              >
                <InputLabel id="db-table-select-label">Table</InputLabel>
                <Select
                  labelId="db-table-select-label"
                  label="Table"
                  value={selectedTable}
                  onChange={handleTableChange}
                  disabled={loadingTables}
                >
                  <MenuItem value="">
                    <em>Select a table</em>
                  </MenuItem>
                  {tables.map((table) => (
                    <MenuItem key={table} value={table}>
                      {table}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                onClick={(e) => setDatePickerAnchor(e.currentTarget)}
                sx={pageFilterDatePickerSx(180)}
                aria-label="Date filter"
              >
                <CalendarMonth fontSize="small" color="action" />
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                  {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
                    ? format(dateRange[0].startDate, 'MMM dd, yyyy')
                    : `${format(dateRange[0].startDate, 'MMM dd')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
                </Typography>
              </Box>
              <Popover
                open={datePickerOpen}
                anchorEl={datePickerAnchor}
                onClose={() => setDatePickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: { maxWidth: '100vw', overflow: 'auto' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRight: { md: '1px solid' },
                      borderBottom: { xs: '1px solid', md: 'none' },
                      borderColor: 'divider',
                      minWidth: 140,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      Quick Select
                    </Typography>
                    <Stack spacing={0.5}>
                      {[
                        { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                        { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                        { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                        { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                        {
                          label: 'This Month',
                          getValue: () => {
                            const now = new Date();
                            return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                          },
                        },
                        {
                          label: 'Last Month',
                          getValue: () => {
                            const now = new Date();
                            return {
                              startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                              endDate: new Date(now.getFullYear(), now.getMonth(), 0),
                            };
                          },
                        },
                      ].map((preset) => (
                        <Button
                          key={preset.label}
                          size="small"
                          variant="text"
                          onClick={() => {
                            const { startDate, endDate } = preset.getValue();
                            setDateRange([{ startDate, endDate, key: 'selection' }]);
                          }}
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.75,
                            color: 'text.primary',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      '& .rdrCalendarWrapper': {
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      },
                      '& .rdrMonth': {
                        width: { xs: '280px', sm: '300px' },
                      },
                    }}
                  >
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      moveRangeOnFirstSelection={false}
                      months={isMobile ? 1 : 2}
                      direction={isMobile ? 'vertical' : 'horizontal'}
                      rangeColors={[PRIMARY]}
                      staticRanges={[]}
                      inputRanges={[]}
                      maxDate={new Date()}
                    />
                    <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <TextField
                        label="Start Time"
                        type="time"
                        size="small"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        fullWidth
                      />
                      <TextField
                        label="End Time"
                        type="time"
                        size="small"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                      <Button
                        onClick={() => {
                          const today = new Date();
                          setDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                          setStartTime(DEFAULT_START_TIME);
                          setEndTime(DEFAULT_END_TIME);
                        }}
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                      >
                        Reset
                      </Button>
                      <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                        Cancel
                      </Button>
                      <Button onClick={handleApplyDateFilter} variant="contained" size="small">
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Popover>

              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FilterAltOffIcon />}
                onClick={handleClearAllFilters}
                disabled={!selectedTable || loadingData}
                aria-label="Clear all filters and reload default data"
                sx={{
                  minWidth: 140,
                  height: 40,
                  flexShrink: 0,
                }}
              >
                Clear All Filters
              </Button>

              <Button
                variant="contained"
                onClick={handleApply}
                disabled={!selectedTable || loadingData}
                sx={{
                  minWidth: 100,
                  height: 40,
                  flexShrink: 0,
                  ml: 0.5,
                }}
              >
                Apply
              </Button>
            </Box>

            <Box
              sx={{
                width: '100%',
                overflowX: 'auto',
                // Keep helper/validation text under Value fields visible.
                overflowY: 'visible',
                WebkitOverflowScrolling: 'touch',
                pb: 2.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  minWidth: 'max-content',
                  whiteSpace: 'nowrap',
                  py: 0.5,
                }}
              >
                {renderFilterControls(filter1, setFilter1Field, 'filter1', 'Column', 'Condition', 'Value')}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, flexShrink: 0, px: 0.5, userSelect: 'none' }}
                >
                  AND
                </Typography>

                {renderFilterControls(filter2, setFilter2Field, 'filter2', 'Column 2', 'Condition 2', 'Value 2')}

                <FormControl size="small" sx={toolbarFieldSx}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    label="Sort By"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    disabled={!selectedTable || columns.length === 0}
                  >
                    <MenuItem value="">
                      <em>Select Column</em>
                    </MenuItem>
                    {columns.map((col) => (
                      <MenuItem key={col} value={col}>
                        {col}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={toolbarFieldSx}>
                  <InputLabel>Order</InputLabel>
                  <Select
                    label="Order"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    disabled={!selectedTable}
                  >
                    {SORT_ORDERS.map((order) => (
                      <MenuItem key={order.value} value={order.value}>
                        {order.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
            {selectedTable && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {totalRecords.toLocaleString()} records
              </Typography>
            )}
          </Box>

          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: { xs: 400, sm: 480, md: 560 },
                overflow: 'auto',
                width: '100%',
                position: 'relative',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: visibleGridColumns.length > 0 ? 600 : 'auto',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <TableHead>
                  <TableRow>
                    {visibleGridColumns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          ...dataCellSx,
                          fontWeight: 'bold',
                          backgroundColor: (theme) => theme.palette.grey[100],
                          zIndex: 3,
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={stickyActionHeaderSx}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!selectedTable ? (
                    <TableRow>
                      <TableCell colSpan={1} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          {loadingTables ? 'Loading tables…' : 'Select a table to view data'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleGridColumns.length + 1}
                        align="center"
                        sx={{ py: 6 }}
                      >
                        <Typography color="text.secondary">No records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, rowIndex) => (
                      <TableRow
                        key={`row-${rowIndex}`}
                        hover
                        sx={{
                          '&:hover .db-explorer-action-cell': {
                            backgroundColor: (theme) => theme.palette.grey[50],
                          },
                        }}
                      >
                        {visibleGridColumns.map((column) => (
                          <TableCell key={`${rowIndex}-${column}`} sx={dataCellSx}>
                            {renderDataCellValue(row[column])}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          className="db-explorer-action-cell"
                          sx={stickyActionCellSx}
                        >
                          {renderViewAction(row, rowIndex)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {selectedTable && totalPages > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={2}
                boundaryCount={1}
                disabled={loadingData}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth={false}
        fullWidth
        aria-labelledby="db-explorer-row-details-title"
        PaperProps={{
          sx: {
            width: { xs: '95vw', md: '80vw' },
            maxWidth: '1200px',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle id="db-explorer-row-details-title">Row Details</DialogTitle>
        <DialogContent dividers>
          {viewRow && isAlertRulesTable(selectedTable) ? (
            renderAlertRuleDetails(viewRow)
          ) : viewRow && isAttendanceNotificationsTable(selectedTable) ? (
            renderAttendanceNotificationDetails(viewRow)
          ) : viewRow && isFaceRecognitionAuditTable(selectedTable) ? (
            renderFaceRecognitionAuditDetails(viewRow)
          ) : viewRow && isHrmsEmployeesTable(selectedTable) ? (
            renderHrmsEmployeeDetails(viewRow)
          ) : viewRow && isRecordingScheduleTable(selectedTable) ? (
            renderRecordingScheduleDetails(viewRow)
          ) : viewRow && isRoiConfigTable(selectedTable) ? (
            renderRoiConfigDetails(viewRow)
          ) : viewRow ? (
            <Table size="small" aria-label="Row field details">
              <TableBody>
                {Object.entries(viewRow).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 'bold', width: '30%', verticalAlign: 'top' }}>
                      {key}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {value === null || value === undefined ? '—' : String(value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DbExplorer;
